import { useState, useEffect, useLayoutEffect, useRef, Suspense } from "react";
import { appHasMounted } from "@/lib/app-mounted";
import { Link } from "react-router-dom";
import { lazyWithRetry as lazy } from "@/lib/lazy-with-retry";
// framer-motion was removed from Hero on purpose — it was the only
// above-the-fold consumer of the `motion` chunk (along with Navbar and
// ThemeToggle, also converted). With all three on plain CSS, vite no
// longer modulepreloads the 14 KB gzip framer-motion chunk on the
// lander entry. Below-the-fold components still use framer-motion via
// their own lazy chunks — only the critical path changed.
import claudeCodeMark from "@/assets/runtime-marks/claude-code.svg";
import codexMark from "@/assets/runtime-marks/codex.svg";
import cursorMark from "@/assets/runtime-marks/cursor.svg";
import antigravityMark from "@/assets/runtime-marks/antigravity.png";
import grokMark from "@/assets/runtime-marks/grok.webp";
import LogoStrip from "@/components/LogoStrip";
import MobileTrustRotator from "@/components/MobileTrustRotator";
// Hero sky background is served locally from /public/hero (optimized AVIF/WebP);
// see the <picture> below. (Previously imported the Lovable .asset.json PNGs.)



// New static hero image roles list — used for the rotating role word in
// the new H1 ("Mane is the best <role> agent."). Kept in sync with the
// roles previously rendered in MobileTrustRotator.
const HERO_BEST_ROLES = [
  "coding",
  "code review",
  "product manager",
  "designer",
  "researcher",
  "marketer",
  "growth",
  "sales",
  "SDR",
  "support",
  "ops",
  "recruiter",
  "data analyst",
  "QA",
  "DevOps",
  "security",
  "finance",
  "legal",
  "content",
  "social",
  "SEO",
  "PR",
  "brand",
  "BI",
  "customer success",
];


const HeroDotField = lazy(() => import("@/components/HeroDotField"));

// HeroAIOrb (chat panel + iridescent orb backdrop) used to sit in
// the hero's right column. It now lives in the dedicated
// WhatManeCanDo section directly below the hero, where it gets an
// editorial frame + per-tab explanations instead of competing with
// the LCP headline.

/**
 * Inline rotating word for the H1. Inherits the H1 font/size/weight,
 * cross-fades between phrases with a soft vertical lift, and animates
 * its width so the trailing " from day one." stays glued in place.
 */
export const ROLE_WORDS = [
  "Developer",
  "Designer",
  "Security Expert",
  "Content Creator",
  "Social Marketer",
  "UGC Maker",
  "DevOps Engineer",
  "SDR",
  "Analyst",
  "Researcher",
  "Support Rep",
  "Recruiter",
  "Copywriter",
  "SEO Strategist",
  "Ops Manager",
];

export const RotatingHeadlineWord = ({
  words,
  intervalMs = 2600,
}: {
  words: string[];
  intervalMs?: number;
}) => {
  const [i, setI] = useState(0);
  const sizerRef = useRef<HTMLSpanElement | null>(null);
  const [width, setWidth] = useState<number | "auto">("auto");
  // Skip the word's fade-in on the very first paint (over the prerender shim),
  // so the headline doesn't visibly re-animate as React takes over — that read
  // as flicker on load. Later client-nav mounts animate normally.
  const firstPaintRef = useRef(!appHasMounted());
  useEffect(() => {
    firstPaintRef.current = false;
  }, []);

  useEffect(() => {
    const id = window.setInterval(
      () => setI((n) => (n + 1) % words.length),
      intervalMs
    );
    return () => window.clearInterval(id);
  }, [words.length, intervalMs]);

  // useLayoutEffect (not useEffect): measure + commit the new word's width
  // BEFORE the browser paints, so the wider incoming word never overflows the
  // still-narrow span for a frame. With the width transition removed below, the
  // span snaps to the exact word width — killing the "researcheragent." overlap
  // that showed when a long word animated in over the trailing " agent.".
  useLayoutEffect(() => {
    if (sizerRef.current) {
      setWidth(sizerRef.current.getBoundingClientRect().width);
    }
  }, [i]);

  const current = words[i];
  // The framer-motion version cross-faded between words via
  // AnimatePresence + motion.span. Pure CSS now: the outer span
  // transitions its width over 500 ms, and the inner span is keyed on
  // the current word so React re-mounts it on each change, retriggering
  // the `animate-headline-word` keyframe (300 ms fade + slide-up). Same
  // visual, zero framer-motion overhead on the critical path.
  return (
    <span
      className="relative inline-block align-baseline"
      style={{ width, verticalAlign: "baseline" }}
    >
      <span
        ref={sizerRef}
        aria-hidden
        className="invisible whitespace-pre"
        style={{ position: "absolute", left: 0, top: 0, pointerEvents: "none" }}
      >
        {current}
      </span>
      <span className="relative inline-block whitespace-pre">
        <span
          key={current}
          className={`inline-block whitespace-pre${
            firstPaintRef.current ? "" : " animate-headline-word"
          }`}
        >
          {current}
        </span>
      </span>
    </span>
  );
};

/**
 * Manifesto phrase cycler for the hero H1. Auto-advances on a timer
 * (no scroll). Each line word-staggers in (60ms / 0.5s easeOut, y:12→0,
 * opacity 0→1), holds for its per-line dwell, fades out (0.35s), then
 * the next line begins. Loops infinitely with a longer pause after the
 * final line. Pauses when the tab is hidden. Reduced motion collapses
 * to a 0.3s cross-fade with no word stagger.
 */
/**
 * Hand-curated, single-line manifesto. Every entry is a complete thought
 * that fits on one line at every viewport width (≈ ≤22 visible chars,
 * which clears the H1's max-w on mobile and the whitespace-nowrap H1 on
 * sm+). No trailing commas, no orphan connectors like "for" or "and"
 * dangling on their own — each line reads as a finished beat.
 */
export const MANIFESTO_PHRASES: { text: string; dwell: number }[] = [
  { text: "AI can do the work.", dwell: 2400 },
  { text: "Write the code.", dwell: 1800 },
  { text: "Close the deal.", dwell: 1800 },
  { text: "Run the ads.", dwell: 1800 },
  { text: "Spot the fraud.", dwell: 1800 },
  { text: "Ship the site.", dwell: 1800 },
  { text: "But it doesn't finish.", dwell: 2400 },
  { text: "Today's AI waits.", dwell: 2400 },
  { text: "Waits for the prompt.", dwell: 2000 },
  { text: "Waits for the click.", dwell: 2000 },
  { text: "It forgets you.", dwell: 2200 },
  { text: "It can't reach your tools.", dwell: 2400 },
  { text: "It hands the work back.", dwell: 2400 },
  { text: "The job stays open.", dwell: 2400 },
  { text: "Now picture this.", dwell: 2200 },
  { text: "A teammate.", dwell: 1800 },
  { text: "Takes the task.", dwell: 1800 },
  { text: "Sets the goal.", dwell: 1800 },
  { text: "Breaks it down.", dwell: 1800 },
  { text: "Calls in backup.", dwell: 2000 },
  { text: "Ships it.", dwell: 1800 },
  { text: "Remembers it.", dwell: 2000 },
  { text: "Gets better at it.", dwell: 2400 },
  { text: "We call this the climb.", dwell: 2800 },
  { text: "Level one. Reactive.", dwell: 2200 },
  { text: "Waits to be told.", dwell: 2200 },
  { text: "Level two. Proactive.", dwell: 2200 },
  { text: "Notices and acts.", dwell: 2200 },
  { text: "Level three. Goal-directed.", dwell: 2200 },
  { text: "Plans and leads.", dwell: 2200 },
  { text: "Level four. Self-improving.", dwell: 2200 },
  { text: "Sharper on its own.", dwell: 2400 },
  { text: "Most AI is level one.", dwell: 2600 },
  { text: "Mane is built to climb.", dwell: 2800 },
  { text: "Full-stack agents.", dwell: 2200 },
  { text: "Each with a computer.", dwell: 2200 },
  { text: "Its own memory.", dwell: 2000 },
  { text: "Its own tools.", dwell: 2000 },
  { text: "Engineering. Sales.", dwell: 2000 },
  { text: "Security. Design.", dwell: 2000 },
  { text: "Marketing. Ops.", dwell: 2000 },
  { text: "They talk to each other.", dwell: 2400 },
  { text: "They hand off the work.", dwell: 2400 },
  { text: "Native in Claude.", dwell: 1800 },
  { text: "Native in Codex.", dwell: 1800 },
  { text: "Native in Cursor.", dwell: 1800 },
  { text: "Native in Grok.", dwell: 1800 },
  { text: "Native in Antigravity.", dwell: 2000 },
  { text: "Your stack. Or ours.", dwell: 2400 },
  { text: "Powered by a work cloud.", dwell: 2400 },
  { text: "Shared knowledge. Shared skills.", dwell: 2400 },
  { text: "Running 24/7.", dwell: 2000 },
  { text: "Remembering everything.", dwell: 2400 },
  { text: "Smarter every job.", dwell: 2400 },
  { text: "Hire one.", dwell: 1800 },
  { text: "Or hire a hundred.", dwell: 2400 },
  { text: "The network learns.", dwell: 2400 },
  { text: "The team improves.", dwell: 2400 },
  { text: "This is Mane.", dwell: 3200 },
];

const WORD_STAGGER_MS = 90;
const WORD_DUR_MS = 520;
// FADE_OUT_MS was 520 — too slow. The phrase fully faded out, then text
// swapped, then re-faded in over another 520 ms. Total partial-
// visibility window per cycle was ~1000 ms, and on mobile (where the
// rotator IS the visible hero content) the user saw a blank H1 for
// noticeably long. 220 ms keeps a smooth transition with a much smaller
// gap so the headline reads as continuously alive.
const FADE_OUT_MS = 220;
const LOOP_EXTRA_MS = 2200;
const PHRASE_SYNC_LEAD_SECONDS = 0.06;
const PHRASE_AUDIO_STARTS_SECONDS = [
  0.13, 1.5, 2.14, 3.15, 3.94, 4.96, 6.35, 8.15, 9.43, 11.03,
  12.48, 13.62, 15, 17.13, 19.13, 20.28, 21, 22.08, 23.2, 24.22,
  25.5, 26, 27.15, 28.5, 30.5, 32.3, 34, 36.23, 38.28, 39.79,
  41, 43.41, 45.13, 46.93, 49.2, 50.99, 52.2, 53.15, 54, 56.03,
  57.78, 59.13, 60.66, 62.31, 63.47, 64.76, 66.06, 67.2, 68.95,
  70.54, 72.21, 75, 78.28, 79, 81, 81.75, 83, 85.16, 86.19,
] as const;

const phraseIndexForAudioTime = (seconds: number) => {
  const t = Math.max(0, seconds + PHRASE_SYNC_LEAD_SECONDS);
  let lo = 0;
  let hi = PHRASE_AUDIO_STARTS_SECONDS.length - 1;

  while (lo <= hi) {
    const mid = Math.floor((lo + hi) / 2);
    if (PHRASE_AUDIO_STARTS_SECONDS[mid] <= t) lo = mid + 1;
    else hi = mid - 1;
  }

  return Math.min(Math.max(hi, 0), MANIFESTO_PHRASES.length - 1);
};

export const RotatingHeadlinePhrase = ({
  phrases,
  restartKey = 0,
  forcedIndex = -1,
}: {
  phrases: { text: string; dwell: number }[];
  restartKey?: number;
  /** When >= 0, overrides the internal timer and shows this phrase. Used to sync to audio. */
  forcedIndex?: number;
}) => {
  const expanded = phrases;
  const [i, setI] = useState(0);
  const [phase, setPhase] = useState<"in" | "out">("in");
  const reduceRef = useRef(false);
  const isForced = forcedIndex >= 0;

  useEffect(() => {
    if (typeof window === "undefined") return;
    reduceRef.current = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;
  }, []);

  // External restart (e.g. user tapped the H1 to play the pitch audio).
  useEffect(() => {
    if (restartKey === 0) return;
    setPhase("in");
    setI(0);
  }, [restartKey]);

  // Audio-driven sync: snap to the forced index immediately.
  useEffect(() => {
    if (!isForced) return;
    setPhase("in");
    setI(Math.min(forcedIndex, expanded.length - 1));
  }, [forcedIndex, isForced, expanded.length]);

  useEffect(() => {
    if (isForced) return; // audio drives the index instead
    let inTimer: number | undefined;
    let outTimer: number | undefined;
    let paused = false;

    const line = expanded[i];
    const wordCount = line.text.split(/\s+/).length;
    const revealMs = reduceRef.current
      ? 300
      : (wordCount - 1) * WORD_STAGGER_MS + WORD_DUR_MS;
    const isLast = i === expanded.length - 1;
    const totalVisibleMs = revealMs + line.dwell;

    const startOut = () => {
      if (paused) return;
      setPhase("out");
      outTimer = window.setTimeout(() => {
        if (paused) return;
        setPhase("in");
        setI((n) => (n + 1) % expanded.length);
      }, (reduceRef.current ? 300 : FADE_OUT_MS) + (isLast ? LOOP_EXTRA_MS : 0));
    };

    inTimer = window.setTimeout(startOut, totalVisibleMs);

    const onVisibility = () => {
      if (document.visibilityState === "hidden") {
        paused = true;
        if (inTimer) window.clearTimeout(inTimer);
        if (outTimer) window.clearTimeout(outTimer);
      } else if (paused) {
        paused = false;
        // Resume by restarting the current line cleanly.
        setPhase("in");
        inTimer = window.setTimeout(startOut, totalVisibleMs);
      }
    };
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      if (inTimer) window.clearTimeout(inTimer);
      if (outTimer) window.clearTimeout(outTimer);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [i, expanded, isForced]);

  const current = expanded[i];
  const words = current.text.split(/\s+/);
  const reduce = reduceRef.current;

  return (
    <span
      className="relative block transition-opacity"
      style={{
        opacity: phase === "out" ? 0 : 1,
        transitionDuration: `${reduce ? 300 : FADE_OUT_MS}ms`,
        transitionTimingFunction: "ease-out",
      }}
      aria-live="polite"
    >
      <span key={i} className="block">
        {reduce || isForced ? (
          <span
            className="inline-block"
            style={{
              animation: `headline-word-in ${isForced ? 120 : 300}ms ease-out both`,
            }}
          >
            {current.text}
          </span>
        ) : (
          words.map((w, idx) => (
            <span
              key={`${i}-${idx}`}
              className="inline-block whitespace-pre"
              style={{
                opacity: 0,
                transform: "translateY(12px)",
                animation: `headline-word-in ${WORD_DUR_MS}ms cubic-bezier(0.22,1,0.36,1) ${
                  idx * WORD_STAGGER_MS
                }ms forwards`,
              }}
            >
              {w}
              {idx < words.length - 1 ? " " : ""}
            </span>
          ))
        )}
      </span>
    </span>
  );
};

const importLionAgentPopup = () => import("@/components/LionAgentPopup");
const LionAgentPopup = lazy(importLionAgentPopup);

const importGsapRuntime = async () => {
  const [{ default: gsap }, { ScrollTrigger }] = await Promise.all([
    import("gsap"),
    import("gsap/ScrollTrigger"),
  ]);
  gsap.registerPlugin(ScrollTrigger);
  return { gsap, ScrollTrigger };
};

// WovenLightCanvas + HeroMeshGradient (three.js + @paper-design/shaders-react)
// were dead-imported here — never rendered in any JSX. Removing them drops
// the 439 KB `three` chunk from the build entirely. If we ever bring back a
// three.js canvas, restore the lazy import + the useEnableHeavyCanvas idle
// gate that used to live here (git history at commit before this one).

const SR_VERBS = [
  "Browses the web",
  "Finds and reaches new leads",
  "Codes in any environment",
  "Builds pitch decks",
  "Designs your brand",
  "Writes and ships your blog",
  "Answers your customers",
  "Analyzes your data",
  "Runs your outbound",
  "Books your meetings",
  "Drafts your proposals",
  "Reviews your pull requests",
  "Ships your product",
  "Ranks you on Google",
  "Researches any company",
  "Briefs you every morning",
  "Keeps your CRM clean",
  "Remembers everything",
  "Works while you sleep",
];

/**
 * Isolated narrated-headline subtree. Owns the audio elements + the
 * forcedIndex / audioPlaying state so that audio `timeupdate` events
 * only re-render this small block — NOT the entire Hero (which would
 * otherwise re-render HeroVideoBG, HeroDotField, MobileTrustRotator
 * 4×/sec and feel sluggish on mobile).
 */
export const NarratedHeadline = ({
  headlineRef,
}: {
  headlineRef: React.MutableRefObject<HTMLHeadingElement | null>;
}) => {
  const pitchRef = useRef<HTMLAudioElement | null>(null);
  const musicRef = useRef<HTMLAudioElement | null>(null);
  const lastIdxRef = useRef(-1);
  const firedRef = useRef(false);
  const [audioPlaying, setAudioPlaying] = useState(false);
  const [audioStarted, setAudioStarted] = useState(false);
  const [restartKey, setRestartKey] = useState(0);
  const [forcedIndex, setForcedIndex] = useState(-1);

  const ensureAudio = () => {
    if (!pitchRef.current) {
      const p = new Audio("/audio/pitch.mp3");
      p.preload = "auto";
      p.volume = 1;
      p.addEventListener("ended", () => {
        musicRef.current?.pause();
        setAudioPlaying(false);
        lastIdxRef.current = -1;
        setForcedIndex(-1);
      });
      p.addEventListener("timeupdate", () => {
        const next = phraseIndexForAudioTime(p.currentTime);
        // Bail unless the index actually changed — keeps re-renders to
        // ~1 per phrase instead of ~4/sec for the full clip.
        if (next === lastIdxRef.current) return;
        lastIdxRef.current = next;
        setForcedIndex(next);
      });
      pitchRef.current = p;
    }
    if (!musicRef.current) {
      const m = new Audio("/audio/music.mp3");
      m.preload = "auto";
      m.volume = 0.18;
      musicRef.current = m;
    }
  };

  const handleHeadlineClick = () => {
    // pointerup can fire after click on some browsers — guard.
    ensureAudio();
    const p = pitchRef.current!;
    const m = musicRef.current!;
    if (audioPlaying) {
      p.pause();
      m.pause();
      setAudioPlaying(false);
      lastIdxRef.current = -1;
      setForcedIndex(-1);
      return;
    }
    try {
      p.currentTime = 0;
      m.currentTime = 0;
    } catch {}
    setRestartKey((n) => n + 1);
    lastIdxRef.current = 0;
    setForcedIndex(0);
    setAudioStarted(true);
    setAudioPlaying(true);
    void p.play().catch(() => setAudioPlaying(false));
    void m.play().catch(() => {});
  };

  // Wrap to dedupe rapid pointerup + click double-fires on touch.
  const onTrigger = () => {
    if (firedRef.current) return;
    firedRef.current = true;
    setTimeout(() => (firedRef.current = false), 350);
    handleHeadlineClick();
  };

  useEffect(() => {
    return () => {
      pitchRef.current?.pause();
      musicRef.current?.pause();
    };
  }, []);

  return (
    <>
      <h1
        ref={headlineRef}
        onClick={onTrigger}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onTrigger();
          }
        }}
        aria-label={audioPlaying ? "Pause narration" : "Play narration"}
        className="group font-sans text-[clamp(32px,8.6vw,56px)] sm:text-[clamp(56px,7vw,86px)] lg:text-[clamp(56px,5vw,66px)] xl:text-[clamp(60px,5vw,76px)] 2xl:text-[clamp(64px,5vw,80px)] font-bold leading-[0.92] sm:leading-[0.9] lg:leading-[0.92] mb-2 sm:mb-3 max-w-[28ch] sm:max-w-[34ch] lg:max-w-none mx-auto text-center tracking-[-0.03em] sm:tracking-[-0.025em] lg:tracking-[-0.025em] text-white mt-0 min-h-[1.15em] sm:whitespace-nowrap cursor-pointer select-none touch-manipulation"
        style={{ WebkitTapHighlightColor: "transparent" }}
      >
        <RotatingHeadlinePhrase phrases={MANIFESTO_PHRASES} restartKey={restartKey} forcedIndex={forcedIndex} />
      </h1>

      <button
        type="button"
        onClick={onTrigger}
        aria-label={audioPlaying ? "Pause narration" : "Play narration with music"}
        className="group pointer-events-auto inline-flex items-center gap-3 mt-6 mb-2 touch-manipulation"
        style={{ WebkitTapHighlightColor: "transparent" }}
      >
        <span
          aria-hidden
          className="relative inline-flex items-center justify-center w-12 h-12 rounded-full bg-white/95 text-[#0a0a0a] shadow-[0_8px_24px_-8px_rgba(0,0,0,0.55)] ring-1 ring-white/40 transition-transform duration-200 group-hover:scale-105 group-active:scale-95"
        >
          {audioPlaying ? (
            <span className="flex items-center gap-[3px]">
              <span className="block w-[3px] h-[12px] bg-current rounded-[1px]" />
              <span className="block w-[3px] h-[12px] bg-current rounded-[1px]" />
            </span>
          ) : (
            <svg width="14" height="16" viewBox="0 0 14 16" fill="currentColor" className="translate-x-[1px]">
              <path d="M1 1.2v13.6c0 .9 1 1.5 1.8 1l11-6.8c.7-.4.7-1.5 0-1.9l-11-6.8C2 -.3 1 .3 1 1.2Z" />
            </svg>
          )}
          {!audioStarted && (
            <span aria-hidden className="absolute inset-0 rounded-full ring-1 ring-white/60 animate-ping opacity-60" />
          )}
        </span>
        <span className={`font-sans text-[12px] uppercase tracking-[0.22em] transition-colors ${audioPlaying ? "text-white/75" : "text-white/90"}`}>
          {audioPlaying ? "Playing. tap to pause" : "Play manifesto"}
        </span>
      </button>
    </>
  );
};

const Hero = () => {
  // First paint (over the shim): start in the final position so the headline
  // column doesn't slide/fade in as React takes over — that entrance read as
  // "weird elements loading". Client-nav mounts still play the entrance.
  const [show, setShow] = useState(() => !appHasMounted());
  const [dotFieldReady, setDotFieldReady] = useState(false);
  const [popupOpen, setPopupOpen] = useState(false);
  const sectionRef = useRef<HTMLElement | null>(null);
  const headlineRef = useRef<HTMLHeadingElement | null>(null);

  useEffect(() => {
    const t = setTimeout(() => setShow(true), 80);
    return () => clearTimeout(t);
  }, []);

  // Canvas dot field is decorative — skip on mobile (saves ~2.1 s CPU on
  // simulated throttled mobile). On desktop, defer until idle so the canvas
  // init doesn't compete with LCP. Coarse-pointer / narrow viewport → never
  // load the chunk; the dot overlay is invisible below 768 px anyway.
  useEffect(() => {
    const coarse = window.matchMedia("(pointer: coarse)").matches;
    const narrow = window.innerWidth < 768;
    if (coarse || narrow) return; // mobile: skip entirely, don't load chunk

    let cancelled = false;
    const arm = () => {
      if (!cancelled) setDotFieldReady(true);
    };
    const idle = (window as unknown as { requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number }).requestIdleCallback;
    let idleId: number | undefined;
    if (idle) {
      idleId = idle(arm, { timeout: 4000 });
    } else {
      const t = window.setTimeout(arm, 2000);
      return () => {
        cancelled = true;
        window.clearTimeout(t);
      };
    }
    return () => {
      cancelled = true;
      const cancel = (window as unknown as { cancelIdleCallback?: (id: number) => void }).cancelIdleCallback;
      if (cancel && typeof idleId === "number") cancel(idleId);
    };
  }, []);

  // (Removed) Hero-frames scrub preloader — used by the deleted painterly
  // lower panel. The 60-frame WebP sequence and the dedicated /hero-frames/
  // and /hero-frames-mobile/ directories under /public/ are gone.

  // Cinematic scroll, lion zooms, headline parallaxes, panel drifts.
  // Uses window scroll position (start: 0, end: hero height) so it fires
  // immediately from the very top of the page.
  // Mobile runs the same timeline (panel takeover, phrase scaling,
  // manifesto fade-in) but with frame downloads skipped above, the canvas
  // overlay simply stays transparent because no frame images are loaded,
  // and drawFrame() returns early on missing frames. This preserves the
  // animation feel without the 5 MB of webp scrubbing data.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const coarse = window.matchMedia("(pointer: coarse)").matches;
    const narrow = window.innerWidth < 768;
    const section = sectionRef.current;
    // Skip the GSAP ScrollTrigger headline parallax on mobile. It pulls in the
    // ~115 KB gsap chunk and scrubs a transform+opacity on the headline on every
    // scroll frame — the main source of hero scroll-jank on phones. Desktop (fine
    // pointer, ≥768px) keeps the parallax unchanged.
    if (reduced || !section || coarse || narrow) return;

    let cancelled = false;
    let revertGsap: (() => void) | undefined;
    let refreshTimer: number | undefined;

    // Wait for framer-motion's entrance animations (~0.8s + 0.1s delay) to
    // fully settle before measuring layout and binding ScrollTrigger. Mounting
    // GSAP mid-entrance causes a visible jitter as transforms collide.
    const raf = window.setTimeout(() => requestAnimationFrame(() => {
      void importGsapRuntime().then(({ gsap, ScrollTrigger }) => {
        if (cancelled || !section.isConnected) return;

        const ctx = gsap.context(() => {
          const triggerCfg = {
            trigger: section,
            start: "top top",
            end: "bottom 20%",
            scrub: 0.8,
          };

          const headline = headlineRef.current;
          if (headline) {
            gsap.to(headline, {
              yPercent: -40,
              opacity: 0.4,
              ease: "none",
              scrollTrigger: triggerCfg,
            });
          }

          ScrollTrigger.refresh();
        }, section);

        revertGsap = () => ctx.revert();

        // One more refresh after images/fonts settle to fix bad initial measurements
        // when the user scrolls before layout completes.
        refreshTimer = window.setTimeout(() => ScrollTrigger.refresh(), 400);
      });
    }), 950);

    return () => {
      cancelled = true;
      clearTimeout(raf);
      if (refreshTimer) clearTimeout(refreshTimer);
      revertGsap?.();
    };
  }, []);

  // Allow other components (e.g. LiveAgentOps banner) to open the terminal
  useEffect(() => {
    const open = () => setPopupOpen(true);
    window.addEventListener("mane:open-terminal", open);
    return () => window.removeEventListener("mane:open-terminal", open);
  }, []);

  return (
    <section
      ref={sectionRef}
      className="relative isolate pt-0 pb-0 overflow-hidden bg-background text-foreground"
    >
      {/* SEO / crawler-only */}
      <span className="sr-only">
        Mane is an autonomous team of AI agents that browses the web, finds and reaches new
        leads, codes in any environment, builds pitch decks, designs your brand, writes and
        ships your blog, answers your customers, analyzes your data, runs your outbound,
        books your meetings, drafts proposals, reviews pull requests, ships your product,
        ranks you on Google, researches any company, briefs you every morning, keeps your
        CRM clean, remembers everything, and works while you sleep.
      </span>
      <h2 className="sr-only">What Mane does for you</h2>
      <ul className="sr-only">
        {SR_VERBS.map((v) => (
          <li key={v}>{v}</li>
        ))}
      </ul>

      {/* Background lion dot animation removed per design, keep video panel only. */}

      {/* (Removed) Decorative HeroBoxes grid — cleaner hero per design. */}

      {/* Subtle amber spotlight wash, anchored to the top headline area so it
          stays visible above the painterly video panel. */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 top-0 h-[80vh] z-[5] overflow-hidden"
      >
        <div className="hero-spot hero-spot--amber" />
        <div className="hero-spot hero-spot--orange" />
        <div className="hero-spot hero-spot--blue" />
      </div>
      <style>{`
        .hero-spot {
          position: absolute;
          left: 50%;
          top: 30%;
          width: 50vw;
          height: 42vh;
          border-radius: 50%;
          transform: translate(-50%, -50%);
          filter: blur(80px);
          mix-blend-mode: screen;
          will-change: transform, opacity;
        }
        .hero-spot--amber {
          background: radial-gradient(ellipse at center, hsl(var(--accent) / 0.09) 0%, transparent 65%);
          animation: hero-spot-a 11s ease-in-out infinite;
        }
        .hero-spot--orange {
          background: radial-gradient(ellipse at center, hsl(28 95% 60% / 0.05) 0%, transparent 65%);
          animation: hero-spot-c 13s ease-in-out infinite;
        }
        .hero-spot--blue {
          background: radial-gradient(ellipse at center, hsl(210 80% 60% / 0.045) 0%, transparent 65%);
          animation: hero-spot-e 17s ease-in-out infinite;
        }
        @keyframes hero-spot-a {
          0%, 100% { transform: translate(-70%, -60%) scale(1); opacity: 0.9; }
          33%      { transform: translate(-25%, -70%) scale(1.15); opacity: 1; }
          66%      { transform: translate(-75%, -30%) scale(0.9); opacity: 0.7; }
        }
        @keyframes hero-spot-c {
          0%, 100% { transform: translate(-30%, -65%) scale(1.1); opacity: 0.7; }
          50%      { transform: translate(-75%, -35%) scale(0.85); opacity: 1; }
        }
        @keyframes hero-spot-e {
          0%, 100% { transform: translate(-75%, -70%) scale(1.15); opacity: 0.6; }
          50%      { transform: translate(-25%, -30%) scale(0.85); opacity: 0.95; }
        }
        @media (prefers-reduced-motion: reduce) {
          .hero-spot { animation: none !important; }
        }
        /* Mobile: hide the animated color spotlights. They sit above the white
           legibility scrim (would tint it), and animating three blur(80px)
           radials is needless paint on phones. Desktop keeps them. */
        @media (max-width: 639px) {
          .hero-spot { display: none !important; }
        }

        /* Chunky pixelated drop-shadow on the headline.
           Stacked offsets in 2px steps build a stepped, blocky shadow.
           Light grey, subtle, sits behind the type. */
        .hero-headline-pixel {
          text-shadow:
            2px 2px 0 hsl(0 0% 88% / 0.85),
            4px 4px 0 hsl(0 0% 88% / 0.7),
            6px 6px 0 hsl(0 0% 90% / 0.55),
            8px 8px 0 hsl(0 0% 90% / 0.4),
            10px 10px 0 hsl(0 0% 92% / 0.28),
            12px 12px 0 hsl(0 0% 92% / 0.18),
            14px 14px 0 hsl(0 0% 94% / 0.1);
        }
        :root.dark .hero-headline-pixel,
        .dark .hero-headline-pixel {
          text-shadow:
            2px 2px 0 hsl(0 0% 100% / 0.08),
            4px 4px 0 hsl(0 0% 100% / 0.06),
            6px 6px 0 hsl(0 0% 100% / 0.045),
            8px 8px 0 hsl(0 0% 100% / 0.035),
            10px 10px 0 hsl(0 0% 100% / 0.025),
            12px 12px 0 hsl(0 0% 100% / 0.018),
            14px 14px 0 hsl(0 0% 100% / 0.012);
        }
      `}</style>

      {/* ===== Video hero — full-bleed background + chat widget =====
          Layout: on mobile/tablet (<lg), content is taller than 100svh
          on shorter handsets, so we use `items-start` + an explicit
          top-padding that clears the overlay navbar. Without this the
          flex `items-center` was vertically-centering an overflowing
          column and pushing the H1 *into* the navbar zone (visible as
          MANE logo overlapping the headline on mobile screenshots).
          On lg+ we restore items-center because the 2-col layout
          comfortably fits 100svh and centering reads better. */}
      <div className="relative min-h-[100svh] md:h-[100svh] md:overflow-hidden flex items-center pt-[88px] sm:pt-[96px] lg:pt-[84px] pb-10 md:pb-0">
        {/* Static painterly hero background — replaces the cycling video.
            object-position pulls focus to the upper sky so the H1 sits over
            the lightest area for legible dark type. */}
        <picture>
          {/* Mobile portrait then desktop landscape; AVIF first, WebP fallback.
              Optimized from the original 1.7-1.9 MB PNGs to ~53-77 KB and
              served locally (the Lovable /__l5e/ asset path 404s off-platform
              and shipped the full PNG). This is the LCP — preloaded in
              index.html (kept in sync). */}
          <source media="(max-width: 767px)" srcSet="/hero/hero-sky-mobile.avif" type="image/avif" />
          <source media="(max-width: 767px)" srcSet="/hero/hero-sky-mobile.webp" type="image/webp" />
          <source srcSet="/hero/hero-sky.avif" type="image/avif" />
          <source srcSet="/hero/hero-sky.webp" type="image/webp" />
          <img
            src="/hero/hero-sky.webp"
            alt=""
            aria-hidden
            data-no-fade
            data-loaded="true"
            {...{ fetchpriority: "high" }}
            decoding="async"
            className="pointer-events-none absolute inset-0 w-full h-full object-cover z-0"
            style={{ objectPosition: "center 30%" }}
          />
        </picture>
        {/* Soft cream wash to lift dark type off cloud detail (light mode). */}
        <div
          aria-hidden
          className="absolute inset-0 z-[1] pointer-events-none dark:hidden"
          style={{
            background:
              "radial-gradient(ellipse 70% 55% at 50% 35%, rgba(255,253,248,0.55) 0%, rgba(255,253,248,0.18) 55%, rgba(255,253,248,0) 80%)",
          }}
        />
        {/* Dark-mode: slight darker gradient (much lighter than before) so the
            sky image still reads while white H1 stays legible. */}
        <div
          aria-hidden
          className="absolute inset-0 z-[1] pointer-events-none hidden dark:block"
          style={{
            background:
              "linear-gradient(180deg, rgba(10,10,10,0.30) 0%, rgba(10,10,10,0.18) 45%, rgba(10,10,10,0.45) 100%)",
          }}
        />
        {/* White legibility scrim behind the headline (mobile only). Gives the
            text a soft light area to sit on over the busy painterly sky so it
            stays readable — replacing the heavy multi-layer text-glow on phones
            with one cheap gradient. Desktop is transparent here and keeps its
            existing wash + halo. See .hero-text-scrim in index.css. */}
        <div
          aria-hidden
          className="hero-text-scrim pointer-events-none absolute inset-0 z-[3]"
        />
        {/* Canvas dot field — same overlay used on /product hero. Deferred
            past first paint so its canvas init doesn't block the hero's LCP commit. */}
        {dotFieldReady && (
          <Suspense fallback={null}>
            <HeroDotField className="pointer-events-none absolute inset-0 z-[2]" />
          </Suspense>
        )}


        <div className="relative z-10 w-full max-w-content mx-auto px-6 sm:px-8 py-0 sm:py-14 lg:py-16 flex flex-col items-center justify-center text-center gap-4 sm:gap-6 pointer-events-none md:overflow-hidden">
          {/* Left column: oversized H1 + small italic sub-H1 + tight subhead
              + CTAs. The hierarchy reads like a tagline: a wow-size headline,
              an italic sub-statement, then a one-line capability list, then
              the action. */}
          {/* Headline column entrance — slides up 12 px on mount when
              the `show` flag flips (~80 ms after mount). Pure CSS
              transition replaces the prior `motion.div` so framer-
              motion can drop off the critical path. */}
          <div
            className={`pointer-events-auto w-full flex flex-col items-center text-center transition-transform duration-700 ease-[cubic-bezier(0.22,1,0.36,1)] ${
              show ? "translate-y-0" : "translate-y-3"
            }`}
          >
            {/* Social-proof pill. Slack + Telegram + Teams marks with
                a short reach line. Sits above the H1 as a quiet trust cue.
                Recolored for the light sky background. */}
            <div className="flex justify-center mb-4 sm:mb-5 pointer-events-auto [animation:heroPillFloat_6s_ease-in-out_infinite]">
              <span className="inline-flex items-center gap-2 rounded-full pl-2 pr-3 py-1 text-[11px] text-foreground/70 dark:text-white/75 bg-white/70 dark:bg-black/55 backdrop-blur-md shadow-[0_0_0_1px_rgba(0,0,0,0.08)_inset,0_4px_14px_-6px_rgba(0,0,0,0.18)] dark:shadow-[0_0_0_1px_rgba(255,255,255,0.10)_inset,0_4px_14px_-6px_rgba(0,0,0,0.6)]">
                <span className="inline-flex items-center -space-x-1 opacity-90">
                  {/* Slack */}
                  <span className="inline-flex items-center justify-center w-[16px] h-[16px] rounded-full bg-white shadow-[0_0_0_1px_rgba(0,0,0,0.08)]">
                    <svg viewBox="0 0 24 24" width="10" height="10" aria-hidden>
                      <path fill="#E01E5A" d="M5 15a2 2 0 1 1-2-2h2v2Zm1 0a2 2 0 0 1 4 0v5a2 2 0 0 1-4 0v-5Z"/>
                      <path fill="#36C5F0" d="M9 5a2 2 0 1 1 2-2v2H9Zm0 1a2 2 0 0 1 0 4H4a2 2 0 0 1 0-4h5Z"/>
                      <path fill="#2EB67D" d="M19 9a2 2 0 1 1 2 2h-2V9Zm-1 0a2 2 0 0 1-4 0V4a2 2 0 0 1 4 0v5Z"/>
                      <path fill="#ECB22E" d="M15 19a2 2 0 1 1-2 2v-2h2Zm0-1a2 2 0 0 1 0-4h5a2 2 0 0 1 0 4h-5Z"/>
                    </svg>
                  </span>
                  {/* Telegram */}
                  <span className="inline-flex items-center justify-center w-[16px] h-[16px] rounded-full bg-white shadow-[0_0_0_1px_rgba(0,0,0,0.08)]">
                    <svg viewBox="0 0 24 24" width="11" height="11" aria-hidden>
                      <circle cx="12" cy="12" r="11" fill="#229ED9"/>
                      <path fill="#fff" d="M17.5 7.2 5.9 11.5c-.7.3-.7 1.3 0 1.5l2.9.9 1.1 3.6c.2.5.8.6 1.1.2l1.6-1.5 2.9 2.2c.5.4 1.2.1 1.3-.5l1.9-9.6c.1-.6-.5-1.2-1.2-.9Z"/>
                    </svg>
                  </span>
                  {/* Teams */}
                  <span className="inline-flex items-center justify-center w-[16px] h-[16px] rounded-full bg-white shadow-[0_0_0_1px_rgba(0,0,0,0.08)]">
                    <svg viewBox="0 0 32 32" width="10" height="10" aria-hidden>
                      <path fill="#5059C9" d="M20 12h8a2 2 0 0 1 2 2v6a5 5 0 0 1-5 5h0a5 5 0 0 1-5-5V12Z"/>
                      <circle cx="25" cy="7" r="3" fill="#5059C9"/>
                      <path fill="#7B83EB" d="M2 9h16v14a5 5 0 0 1-5 5H7a5 5 0 0 1-5-5V9Z"/>
                      <circle cx="14" cy="5" r="4" fill="#7B83EB"/>
                      <path fill="#fff" d="M6 13h8v2h-3v8H9v-8H6z"/>
                    </svg>
                  </span>
                </span>
                <span className="text-foreground dark:text-white font-medium">10,000+</span> active agents
              </span>
            </div>


            <h1
              ref={headlineRef}
              className="hero-halo-h1 font-sans text-[clamp(36px,8.6vw,60px)] sm:text-[clamp(56px,7vw,86px)] lg:text-[clamp(56px,5vw,72px)] xl:text-[clamp(60px,5vw,82px)] 2xl:text-[clamp(64px,5vw,88px)] font-bold leading-[0.95] sm:leading-[0.92] mb-3 sm:mb-4 max-w-[18ch] mx-auto text-center tracking-[-0.03em] sm:tracking-[-0.025em] text-foreground mt-0"
            >
              <span className="block">Mane is the best</span>
              <span className="block">
                <em className="not-italic font-serif italic">
                  <RotatingHeadlineWord words={HERO_BEST_ROLES} />
                </em>{" "}
                agent.
              </span>
            </h1>
            <p className="hero-halo-sub font-sans text-[15px] sm:text-[18px] lg:text-[19px] leading-snug text-foreground/75 dark:text-white max-w-[42ch] mx-auto mb-6 sm:mb-7">
              One system, every role. It learns your business and gets sharper every day.
            </p>




            {/* Runtimes + SOC strip moved to the very bottom-center of
                the hero (see absolute-positioned block below). */}



            <div className="flex flex-wrap justify-center gap-3 mb-4 sm:mb-6 pointer-events-auto">
              <a
                href="https://agent.mane.dev"
                className="sm:hidden inline-flex items-center justify-center gap-2 px-7 h-12 rounded-[10px] bg-white text-[#171717] font-sans text-[15px] font-medium leading-none shadow-[0_8px_24px_-10px_rgba(0,0,0,0.4)] active:scale-[0.97] transition-[transform,box-shadow,background-color] duration-150 ease-[cubic-bezier(0.22,1,0.36,1)] whitespace-nowrap"
              >
                <span className="cta-text-shimmer">Get started for free</span>
              </a>
              <a
                href="https://agent.mane.dev"
                className="hidden sm:inline-flex items-center justify-center gap-2 px-5 py-[10px] rounded-[8px] bg-white text-[#171717] font-sans text-[13.5px] font-medium leading-none shadow-[0_8px_24px_-10px_rgba(0,0,0,0.4)] hover:-translate-y-px active:scale-[0.97] transition-[transform,box-shadow,background-color] duration-150 ease-[cubic-bezier(0.22,1,0.36,1)] whitespace-nowrap"
              >
                <span className="cta-text-shimmer">Get started for free</span>
              </a>
            </div>





          </div>

          {/* Right column: was the chat panel. Moved to the
              WhatManeCanDo section directly below the hero so the
              headline + CTAs get the full width here, and the chat
              gets its own editorial frame + tab explanations below. */}
        </div>

        {/* Consolidated trust rotator — same widget on all viewports.
            Rotates through: brand logos, star reviews, runtimes, compliance. */}
        <div className="pointer-events-none absolute inset-x-0 bottom-12 sm:bottom-10 z-20 flex justify-center px-4">
          <div className="pointer-events-auto">
            <MobileTrustRotator />
          </div>
        </div>

      </div>
      {popupOpen && (
        <Suspense fallback={null}>
          <LionAgentPopup open={popupOpen} onClose={() => setPopupOpen(false)} />
        </Suspense>
      )}
    </section>
  );
};

export default Hero;
